import {
  EmailBlockTypeType,
  EmailDynamicSourceType,
  MarketingLanguageType,
  type EmailBlockInputType,
} from "@/types/graphql/partner.generated";
import {
  LANGUAGE_LABEL_KEYS,
  MARKETING_LANGUAGES,
} from "@/partner/entities/marketing";

export { LANGUAGE_LABEL_KEYS, MARKETING_LANGUAGES };

/**
 * Блок письма в редакторе: та же плоская форма, что `EmailBlockInput`
 * (id блока генерирует клиент, поля заполнены по типу).
 */
export type EditorBlock = EmailBlockInputType;

/** Длины полей, как в MARKETING_LIMITS бэкенда (backend/src/domains/marketing/constants.ts). */
export const EMAIL_TEMPLATE_LIMITS = {
  name: 200,
  subject: 200,
  preheader: 300,
  blocksPerVariant: 50,
  text: 5000,
  label: 100,
  url: 2000,
} as const;

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

/** Адрес кнопки целиком одна переменная: {{productUrl}}, пробелы внутри скобок допустимы. */
const WHOLE_VARIABLE = /^\{\{\s*([a-zA-Z]+)\s*\}\}$/;

/**
 * Адрес кнопки, который целиком одна переменная-адрес из списка сервера
 * (marketingLimits.emailUrlVariables), как isUrlVariable бэкенда. Без
 * списка (запрос не ответил) переменные не принимаются.
 */
const isUrlVariable = (
  url: string | null | undefined,
  urlVariables: readonly string[]
): boolean => {
  const trimmed = url?.trim() ?? "";
  if (trimmed.length > EMAIL_TEMPLATE_LIMITS.url) return false;
  const name = WHOLE_VARIABLE.exec(trimmed)?.[1];
  return Boolean(name && urlVariables.includes(name));
};

/**
 * Подсказки к переменным-адресам: что подставится и в каких письмах. Сам
 * список переменных приходит с сервера, здесь только тексты.
 */
const URL_VARIABLE_HINT_KEYS: Readonly<Record<string, string>> = {
  productUrl: "emailTemplates.blocks.urlVariable.productUrl",
  manageSubscriptionsUrl:
    "emailTemplates.blocks.urlVariable.manageSubscriptionsUrl",
  updateCardUrl: "emailTemplates.blocks.urlVariable.updateCardUrl",
};

const URL_VARIABLE_HINT_FALLBACK_KEY =
  "emailTemplates.blocks.urlVariable.unknown";

/** Ключ подсказки к переменной; переменная новее кабинета: общий текст. */
export const urlVariableHintKey = (name: string): string =>
  Object.prototype.hasOwnProperty.call(URL_VARIABLE_HINT_KEYS, name)
    ? URL_VARIABLE_HINT_KEYS[name]
    : URL_VARIABLE_HINT_FALLBACK_KEY;

export const newBlock = (type: EmailBlockTypeType): EditorBlock => ({
  id: crypto.randomUUID(),
  type,
  text: null,
  label: null,
  url: null,
  imageId: null,
  alt: null,
  contentId: null,
  productId: null,
  source: null,
  required: false,
});

/**
 * Ключ перевода проблемы блока или null. Повторяет validateBlock из
 * EmailTemplateValidator бэкенда, чтобы кнопка «Сохранить» и превью не
 * отправляли заведомо отклоняемый черновик. urlVariables: переменные,
 * которые сервер разрешает адресом кнопки (у картинки их нет).
 */
export function blockIssues(
  block: EditorBlock,
  urlVariables: readonly string[] = []
): string | null {
  const filled = (value?: string | null) => Boolean(value?.trim());
  const safe = (value?: string | null) =>
    Boolean(value && SAFE_URL.test(value.trim()));
  switch (block.type) {
    case EmailBlockTypeType.HeadingType:
    case EmailBlockTypeType.TextType:
      return filled(block.text) ? null : "emailTemplates.errors.incomplete";
    case EmailBlockTypeType.ButtonType:
      if (!filled(block.label)) return "emailTemplates.errors.incomplete";
      return safe(block.url) || isUrlVariable(block.url, urlVariables)
        ? null
        : "emailTemplates.errors.url";
    case EmailBlockTypeType.ImageType:
      if (!block.imageId) return "emailTemplates.errors.incomplete";
      return !block.url || safe(block.url) ? null : "emailTemplates.errors.url";
    case EmailBlockTypeType.ContentCardType:
      return block.contentId ? null : "emailTemplates.errors.incomplete";
    case EmailBlockTypeType.ProductCardType:
      return block.productId ? null : "emailTemplates.errors.incomplete";
    case EmailBlockTypeType.DynamicCardType:
      return block.source ? null : "emailTemplates.errors.incomplete";
    default:
      return null;
  }
}

/** Порядок типов в меню «Добавить блок». */
export const BLOCK_TYPES: EmailBlockTypeType[] = [
  EmailBlockTypeType.HeadingType,
  EmailBlockTypeType.TextType,
  EmailBlockTypeType.ButtonType,
  EmailBlockTypeType.ImageType,
  EmailBlockTypeType.ContentCardType,
  EmailBlockTypeType.ProductCardType,
  EmailBlockTypeType.DynamicCardType,
  EmailBlockTypeType.DividerType,
];

/**
 * Типы, у которых есть форма в BlockEditor (с Task 11 это все восемь).
 * Новый тип блока включается добавлением сюда и веткой в BlockEditor.
 */
export const EDITABLE_BLOCK_TYPES: ReadonlySet<EmailBlockTypeType> = new Set(
  BLOCK_TYPES
);

export const BLOCK_TYPE_LABEL_KEYS: Record<EmailBlockTypeType, string> = {
  [EmailBlockTypeType.HeadingType]: "emailTemplates.blockTypes.heading",
  [EmailBlockTypeType.TextType]: "emailTemplates.blockTypes.text",
  [EmailBlockTypeType.ButtonType]: "emailTemplates.blockTypes.button",
  [EmailBlockTypeType.ImageType]: "emailTemplates.blockTypes.image",
  [EmailBlockTypeType.ContentCardType]: "emailTemplates.blockTypes.contentCard",
  [EmailBlockTypeType.ProductCardType]: "emailTemplates.blockTypes.productCard",
  [EmailBlockTypeType.DynamicCardType]: "emailTemplates.blockTypes.dynamicCard",
  [EmailBlockTypeType.DividerType]: "emailTemplates.blockTypes.divider",
};

/** Источники персональной подборки в порядке списка в форме блока. */
export const DYNAMIC_SOURCES: EmailDynamicSourceType[] = [
  EmailDynamicSourceType.PurchasedType,
  EmailDynamicSourceType.ContinueWatchingType,
  EmailDynamicSourceType.NextStreamType,
  EmailDynamicSourceType.TopProductType,
  EmailDynamicSourceType.EventObjectType,
  EmailDynamicSourceType.AbandonedCheckoutType,
];

export const DYNAMIC_SOURCE_LABEL_KEYS: Record<EmailDynamicSourceType, string> =
  {
    [EmailDynamicSourceType.PurchasedType]:
      "emailTemplates.dynamicSources.purchased",
    [EmailDynamicSourceType.ContinueWatchingType]:
      "emailTemplates.dynamicSources.continueWatching",
    [EmailDynamicSourceType.NextStreamType]:
      "emailTemplates.dynamicSources.nextStream",
    [EmailDynamicSourceType.TopProductType]:
      "emailTemplates.dynamicSources.topProduct",
    [EmailDynamicSourceType.EventObjectType]:
      "emailTemplates.dynamicSources.eventObject",
    [EmailDynamicSourceType.AbandonedCheckoutType]:
      "emailTemplates.dynamicSources.abandonedCheckout",
  };

/** Что попадёт в карточку при реальной отправке, по источнику. */
export const DYNAMIC_SOURCE_HINT_KEYS: Record<EmailDynamicSourceType, string> =
  {
    [EmailDynamicSourceType.PurchasedType]:
      "emailTemplates.dynamicSourceHints.purchased",
    [EmailDynamicSourceType.ContinueWatchingType]:
      "emailTemplates.dynamicSourceHints.continueWatching",
    [EmailDynamicSourceType.NextStreamType]:
      "emailTemplates.dynamicSourceHints.nextStream",
    [EmailDynamicSourceType.TopProductType]:
      "emailTemplates.dynamicSourceHints.topProduct",
    [EmailDynamicSourceType.EventObjectType]:
      "emailTemplates.dynamicSourceHints.eventObject",
    [EmailDynamicSourceType.AbandonedCheckoutType]:
      "emailTemplates.dynamicSourceHints.abandonedCheckout",
  };

/** Язык платформы (код из CurrentPlatform.language) как язык письма, иначе en. */
export const toMarketingLanguage = (
  code?: string | null
): MarketingLanguageType =>
  MARKETING_LANGUAGES.find((language) => language === code?.toLowerCase()) ??
  MarketingLanguageType.EnType;

/**
 * Короткое превью содержимого блока для строки списка блоков. У карточек
 * контента и продукта названия нет в блоке (только id), поэтому строка
 * пустая; подборка подписывается источником в BlockList.
 */
export const blockSummary = (block: EditorBlock): string => {
  const source =
    block.type === EmailBlockTypeType.ButtonType
      ? block.label
      : block.type === EmailBlockTypeType.ImageType
        ? block.alt
        : block.text;
  const line = (source ?? "").replace(/\s+/g, " ").trim();
  return line.length > 80 ? `${line.slice(0, 80)}...` : line;
};
